import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AppPreview.css';

// Four phones showing the quest the way the app renders it. Each phone is an
// iframe on the app's own /preview page (QuestApp web/src/pages/PreviewPage.tsx),
// which draws the app's real views from the fields we post — unsaved edits
// included. Nothing is copied from the app here; only the API's list/detail
// shape is assembled from the editor's state.

const APP_URL = process.env.REACT_APP_APP_PREVIEW_URL || 'https://app.explorecity.ru';
const APP_ORIGIN = new URL(APP_URL).origin;

const SCREENS = [
  ['list', 'Quest list'],
  ['detail', 'Before purchase'],
  ['bought', 'After purchase'],
  ['done', 'Finish screen'],
];

// A typical phone viewport in CSS pixels; the frames are scaled down to fit.
const PHONE_W = 390;
const PHONE_H = 844;
const GAP = 16;

const AppPreview = ({
  questId, selectedTable, questInfo, locationsCount, city,
  promoLong, promoAfter, coverImageIds, price, opensAt, today,
}) => {
  const containerRef = useRef(null);
  const framesRef = useRef({});
  const [scale, setScale] = useState(0.5);
  const [hasAudio, setHasAudio] = useState(false);

  // has_audio in the app: any audio file on any of the quest's points.
  useEffect(() => {
    if (!questId) return undefined;
    let cancelled = false;
    fetch(`/api/quest-audio/${questId}?table=${selectedTable || 'quests_test'}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => { if (!cancelled) setHasAudio(Array.isArray(rows) && rows.length > 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [questId, selectedTable]);

  // Same shapes as the app API's /quests list item and quest details.
  const payload = useMemo(() => {
    const comingSoon = !!(opensAt && today && opensAt > today);
    const name = (questInfo && questInfo.quest_name) || 'Some quest';
    const images = coverImageIds || [];
    return {
      questId,
      list: {
        id: questId,
        name,
        bought: false,
        city: city || '',
        price: price == null ? null : price,
        cover_image_id: images.length ? images[0] : null,
        // Mirrors the API: a hidden voice-over (quest_info.voiceover_hidden) shows no badge.
        has_audio: hasAudio && !(questInfo && questInfo.voiceover_hidden === true),
        locations_count: locationsCount || 0,
        coming_soon: comingSoon,
        opens_at: opensAt || null,
      },
      detail: {
        name,
        long_description: promoLong || '',
        bought_description: promoAfter || '',
        price: price == null ? null : price,
        original_price: null,
        promo_image_ids: [],
        promo_long_image_ids: images,
        coming_soon: comingSoon,
        opens_at: opensAt || null,
      },
      finalMessage: (questInfo && questInfo.final_message) || null,
    };
  }, [questId, questInfo, locationsCount, city, promoLong, promoAfter, coverImageIds, price, opensAt, today, hasAudio]);

  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  // A frame says "ready" once its page is up; answer it with the current fields.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== APP_ORIGIN || !e.data || e.data.type !== 'explorecity-preview-ready') return;
      e.source.postMessage({ type: 'explorecity-preview', payload: payloadRef.current }, APP_ORIGIN);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Every edit goes to all four frames.
  useEffect(() => {
    Object.values(framesRef.current).forEach((frame) => {
      if (frame && frame.contentWindow) {
        frame.contentWindow.postMessage({ type: 'explorecity-preview', payload }, APP_ORIGIN);
      }
    });
  }, [payload]);

  // Two phones per row, scaled to the panel width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const fit = () => {
      const width = el.clientWidth - 2 * GAP;
      // 2 × 6px phone border per frame (AppPreview.css).
      setScale(Math.max(0.2, Math.min(1, (width - GAP - 24) / (2 * PHONE_W))));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="app-preview" ref={containerRef}>
      <div className="app-preview__grid" style={{ gap: GAP }}>
        {SCREENS.map(([screen, label]) => (
          <div key={screen} className="app-preview__cell">
            <div className="app-preview__label">{label}</div>
            <div
              className="app-preview__phone"
              style={{ width: PHONE_W * scale, height: PHONE_H * scale }}
            >
              <iframe
                key={`${questId}-${screen}`}
                ref={(f) => { framesRef.current[screen] = f; }}
                title={label}
                src={`${APP_URL}/preview?screen=${screen}`}
                style={{ width: PHONE_W, height: PHONE_H, transform: `scale(${scale})` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AppPreview;
